import React from 'react';
import { useRouter } from 'next/router';
import { OperationResult } from 'urql';
import * as Sentry from '@sentry/nextjs';
import { Icon } from '@global-ecom/nitro-uds/elements';

import { useAdyen } from 'hooks/useAdyen';
import { useTranslate } from 'hooks/useTranslations';
import {
  AdyenPaymentMethod,
  CancelApplePayMutation,
  CancelApplePayMutationVariables,
  StartAdyenPaymentInput,
  useCancelApplePayMutation,
  useStartAdyenPaymentMutation,
  useUpdateBillingAddressMutation,
  useUpdateShipmentMethodMutation,
  useUpdateShippingAddressMutation,
  useValidateApplePayMerchantMutation,
} from '__generated__/graphql';
import { useServerSidePage } from 'hooks/useServerSidePage';
import { useSiteConfig } from 'hooks/useSiteConfig';
import {
  canStartAppleSheet,
  ensureAddressFields,
  hasAllFieldsForTaxCalculation,
  transformShipmentMethod,
} from 'utils/payments/apple';
import { useApplePay } from 'hooks/useApplePay';
import { PaymentMethod, isServer } from 'utils/constants';
import { addPaymentToSessionStorage } from 'utils/addPaymentToSessionStorage';
import { AnalyticsEvents, event, paymentClickEvent } from 'utils/analytics';
import { useCart } from 'hooks/useCart';
import { useFeature } from 'hooks/useFeature';
import { Button } from 'ui/elements/Button';
import { useEmplifiTrackNewOrderCbStore } from 'store/emplifiTrackNewOrderCbStore';
import { ActiveCartWithoutPaymentMethods } from 'types/cart';

export const AdyenApplePayButton = (props: { disabled?: boolean }) => {
  const canRender = useServerSidePage();

  const t = useTranslate();

  const router = useRouter();

  const [, validateApplePayMerchant] = useValidateApplePayMerchantMutation();

  const getAdyenInstance = useAdyen();

  const [, updateShipmentMethod] = useUpdateShipmentMethodMutation();

  const [, updateShippingAddress] = useUpdateShippingAddressMutation();

  const [, updateBillingAddress] = useUpdateBillingAddressMutation();

  const { applePaySupportedNetworks } = useSiteConfig();

  const { emplifiTrackNewOrderCb } = useEmplifiTrackNewOrderCbStore();
  /**
   * `true` when we set up the adyen SDK and / or remounting the component.
   */
  const [initializing, setInitializing] = React.useState(true);

  const [paymentSheetOpen, setPaymentSheetOpen] = React.useState(false);

  const applePayContainerDomRef = React.useRef<HTMLDivElement>(null);

  const applePayAdyenElementRef = React.useRef<any>(null);

  const stateRef = React.useRef<any>(null);

  const { countryCode, localizeUrlPath, currency, env } = useSiteConfig();

  const isApplePayEnabled = useFeature(PaymentMethod.APPLE_PAY);

  const cancelApplePayCallback = async (
    variables: CancelApplePayMutationVariables
  ): Promise<OperationResult<CancelApplePayMutation>> => {
    setPaymentSheetOpen(false);

    return cancelApplePay(variables);
  };

  const [, cancelApplePay] = useCancelApplePayMutation();

  const [, startAdyenPayment] = useStartAdyenPaymentMutation();

  const shouldInitialize =
    canRender && canStartAppleSheet && isApplePayEnabled && env.adyenClientKey;

  const { cart, cartResult } = useCart({
    pause: isServer || !shouldInitialize,
  });

  const basketId = cartResult.data?.activeCart?.id;

  const failOrder = async ({
    component,
    error,
  }: {
    error?: any;
    component: any;
  }) => {
    component?.unmount();

    window.sessionStorage.removeItem('payment-method');

    if (basketId) {
      await cancelApplePay({ basketId });
    }

    component?.mount(applePayContainerDomRef.current);

    setPaymentSheetOpen(false);

    Sentry.captureException(error);
  };

  const cartHasProducts = cart?.products.some(
    product => product.__typename !== 'GiftCardCartItem'
  );

  const mountApplePayButton = React.useCallback(async (component: any) => {
    try {
      const isAvailable = await (component as any).isAvailable();

      if (isAvailable && applePayContainerDomRef.current) {
        component.mount(applePayContainerDomRef.current);
      }
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      setInitializing(false);
    }
  }, []);

  const {
    createLineItems,
    generateUpdateShippingAddressInputFromAppleEvent,
    getShippingContactUpdateForMissingTaxFields,
    getShippingContactUpdateForShippingAddressMutationError,
    getShippingContactUpdateForSuccess,
    mapAppleEventToAddressInput,
  } = useApplePay({ cartResult });

  const initializeAdyenApplePay = React.useCallback(async () => {
    if (basketId && shouldInitialize) {
      const adyen = await getAdyenInstance();

      const applePayElement = adyen?.create('applepay', {
        onValidateMerchant: async (
          resolve: ApplePaySession['completeMerchantValidation'],
          reject: ApplePaySession['completeMerchantValidation'],
          validationUrl: string
        ) => {
          const operationResult = await validateApplePayMerchant({
            input: { validationUrl, domainName: window.location.host },
          });

          if (operationResult.data?.validateApplePayMerchant) {
            resolve(JSON.parse(operationResult.data.validateApplePayMerchant));
          }

          reject('Could not generate merchant validation response.');
        },
        amount: {
          value: ((cart?.estimatedTotal ?? 0) * 100).toFixed(2),
          currency: currency.code,
        },
        countryCode: countryCode.toUpperCase(),
        supportedNetworks: applePaySupportedNetworks,
        shippingMethods: cart?.shipmentMethodInfo?.availableShipmentMethods
          .filter(m => (cartHasProducts ? true : m.id === 'Email'))
          .map(transformShipmentMethod),
        requiredShippingContactFields: [
          'postalAddress',
          'name',
          'phone',
          'email',
        ],
        requiredBillingContactFields: ['postalAddress', 'name'],
        lineItems: [
          {
            label: t('applePayItemsTotal'),
            amount: (cart?.subTotal ?? 0).toFixed(2),
            type: 'final',
          },
          {
            label: t('applePayTax'),
            amount: (cart?.taxTotal ?? 0).toFixed(2),
            type: 'final',
          },
          {
            label: t('applePayShipment'),
            amount: (cart?.shipment?.shipmentMethod?.price ?? 0).toFixed(2),
            type: 'final',
          },
          {
            label: t('orderDiscount'),
            amount: cart?.orderAdjustments
              .reduce((sum, item) => sum + (item.price ?? 0), 0)
              .toFixed(2),
            type: 'final',
          },
          {
            label: t('appleShippingDiscount'),
            amount: cart?.shippingAdjustments
              .reduce((sum, item) => sum + (item.price ?? 0), 0)
              .toFixed(2),
            type: 'final',
          },
        ],
        onClick: (resolve: () => void) => {
          setPaymentSheetOpen(true);

          resolve();
        },
        onSubmit: (state: any) => {
          stateRef.current = state;
        },
        onShippingContactSelected: async (
          resolve: ApplePaySession['completeShippingContactSelection'],
          _reject: ApplePaySession['completeShippingContactSelection'],
          event: ApplePayJS.ApplePayShippingContactSelectedEvent
        ) => {
          if (!hasAllFieldsForTaxCalculation(event)) {
            return resolve(getShippingContactUpdateForMissingTaxFields());
          }

          const updateShippingAddressOperationResult =
            await updateShippingAddress({
              input: generateUpdateShippingAddressInputFromAppleEvent(event),
            });

          const cartResult =
            updateShippingAddressOperationResult.data?.updateShippingAddress;

          if (!cartResult) {
            return resolve(
              getShippingContactUpdateForShippingAddressMutationError()
            );
          }

          resolve(
            getShippingContactUpdateForSuccess({
              addressResult: cartResult as ActiveCartWithoutPaymentMethods,
            })
          );
        },
        onAuthorized: async (
          resolve: ApplePaySession['completePayment'],
          reject: ApplePaySession['completePayment'],
          event: ApplePayJS.ApplePayPaymentAuthorizedEvent
        ) => {
          if (!cart) return;

          if (
            event.payment.shippingContact?.countryCode?.toUpperCase() !==
            countryCode.toUpperCase()
          ) {
            await cancelApplePayCallback({ basketId: cart.id });

            return reject(
              ApplePaySession.STATUS_INVALID_SHIPPING_POSTAL_ADDRESS
            );
          }

          if (!ensureAddressFields(event.payment.shippingContact)) {
            await cancelApplePayCallback({ basketId: cart.id });

            return reject(
              ApplePaySession.STATUS_INVALID_SHIPPING_POSTAL_ADDRESS
            );
          }

          if (!ensureAddressFields(event.payment.billingContact)) {
            await cancelApplePayCallback({ basketId: cart.id });

            return reject(
              ApplePaySession.STATUS_INVALID_BILLING_POSTAL_ADDRESS
            );
          }

          const [shippingInput, billingInput] =
            mapAppleEventToAddressInput(event);

          const updateShippingAddressPromise = await updateShippingAddress({
            input: shippingInput,
          });

          if (updateShippingAddressPromise.error) {
            await cancelApplePayCallback({ basketId: cart.id });

            return reject(
              ApplePaySession.STATUS_INVALID_SHIPPING_POSTAL_ADDRESS
            );
          }

          const updateBillingAddressOperationResult =
            await updateBillingAddress({
              input: billingInput,
            });

          if (updateBillingAddressOperationResult.error) {
            await cancelApplePayCallback({ basketId: cart.id });

            return reject(
              ApplePaySession.STATUS_INVALID_BILLING_POSTAL_ADDRESS
            );
          }

          const stringifiedPaymentData = JSON.stringify(
            event.payment.token.paymentData
          );

          const input: StartAdyenPaymentInput = {
            basketId,
            adyenPaymentData: {
              applePayToken: Buffer.from(stringifiedPaymentData).toString(
                'base64'
              ),
              paymentMethodType: AdyenPaymentMethod.ApplePay,
            },
          };

          if (typeof stateRef.current?.data?.checkoutAttemptId === 'string') {
            input.adyenPaymentData.checkoutAttemptId =
              stateRef.current.data.checkoutAttemptId;
          }

          if (
            typeof stateRef.current?.data?.clientStateDataIndicator === 'string'
          ) {
            input.adyenPaymentData.clientStateDataIndicator =
              stateRef.current.data.clientStateDataIndicator;
          }

          if (
            typeof stateRef.current?.data?.riskData?.clientData === 'string'
          ) {
            input.adyenPaymentData.riskData = {
              clientData: stateRef.current.data.riskData.clientData,
            };
          }

          const operationResult = await startAdyenPayment({
            input,
          });

          if (
            operationResult.data?.startAdyenPayment?.resultCode !==
              'Authorised' ||
            !operationResult.data?.startAdyenPayment.orderNo
          ) {
            return reject(ApplePaySession.STATUS_FAILURE);
          }

          // no errors -> track order with Emplifi
          emplifiTrackNewOrderCb?.();

          addPaymentToSessionStorage({
            orderNo: operationResult.data.startAdyenPayment.orderNo!,
            paymentMethod: PaymentMethod.APPLE_PAY,
          });

          resolve(ApplePaySession.STATUS_SUCCESS);

          router.push({
            pathname: localizeUrlPath('/checkout/confirmation'),
            query: {
              orderNo: operationResult.data.startAdyenPayment.orderNo,
            },
          });
        },
        onShippingMethodSelected: async (
          resolve: ApplePaySession['completeShippingMethodSelection'],
          _reject: ApplePaySession['completeShippingMethodSelection'],
          event: ApplePayJS.ApplePayShippingMethodSelectedEvent
        ) => {
          if (!cart) return;

          const updateShipmentOperationResult = await updateShipmentMethod({
            input: {
              shippingMethodId: event.shippingMethod.identifier,
              basketId: cart.id,
            },
          });

          if (!updateShipmentOperationResult.data?.updateShipmentMethod) {
            return resolve(createLineItems(cart));
          }

          resolve(
            createLineItems(
              updateShipmentOperationResult.data
                .updateShipmentMethod as ActiveCartWithoutPaymentMethods
            )
          );
        },
        onError: (error: any, component: any) => {
          failOrder({ component, error });
        },
        showPayButton: false,
      });

      applePayAdyenElementRef.current = applePayElement;

      mountApplePayButton(applePayElement);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stable refs
  }, [basketId, shouldInitialize]);

  React.useEffect(() => {
    initializeAdyenApplePay();
  }, [initializeAdyenApplePay]);

  if (!shouldInitialize) return null;

  return (
    <div ref={applePayContainerDomRef} data-test-id="adyen-payment-iframe">
      <Button
        className="w-full h-14 bg-white border-puma-black-700 hover:(bg-neutral-20 border-neutral-20)"
        dataTestId="adyen-apple-pay-button"
        label="Apple Pay"
        disabled={
          props.disabled ||
          paymentSheetOpen ||
          initializing ||
          !cart ||
          cartResult.stale
        }
        loading={initializing || !cart || cartResult.stale}
        lang={router.query.locale as string}
        onClick={e => {
          e.preventDefault();

          applePayAdyenElementRef.current.submit();
          event(AnalyticsEvents.PAYMENT_TYPE_SELECTED, {
            ecommerce: {
              name: AnalyticsEvents.PAYMENT_TYPE_SELECTED,
              value: 'Apple Pay',
            },
          });
          paymentClickEvent();
        }}
        type="button"
      >
        <Icon name="payment-apple-pay" size="9xl" />
      </Button>
    </div>
  );
};
